import { useEffect, useLayoutEffect, useState } from "react"
import { PodpodLogo } from "~/packages/podpod-logo"
import { PrefixDemo } from "~/landing/ArticleStory"
import "~/landing/landing.css"
import { Link } from "@remix-run/react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/packages/dialog"

export default function Index() {
  useLayoutEffect(() => {
    document.documentElement.classList.remove("dark")

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleColorSchemeChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        document.documentElement.classList.remove("dark")
      }
    }

    mediaQuery.addEventListener("change", handleColorSchemeChange)
    return () => {
      mediaQuery.removeEventListener("change", handleColorSchemeChange)
    }
  }, [])

  return (
    <div className="podpod-landing bg-podpod-bg text-gray-12">
      <SunsetDialog />
      <Header />
      <main>
        <div className="px-4 pt-4 pb-[clamp(2.5rem,6vw,5rem)]">
          <div className="hero-panel relative overflow-hidden rounded-pod bg-[url('/images/lander-bg.jpg')] bg-cover bg-center">
            <Hero />
            <FloatingArticles />
          </div>
        </div>
        <Steps />
        <Hosts />
        <Faqs />
      </main>
      <Footer />
    </div>
  )
}

const SunsetDialog = () => {
  return (
    <Dialog defaultOpen>
      <DialogContent className="sunset-notice max-w-[calc(100%-2rem)] sm:max-w-md">
        <DialogHeader className="gap-2 pr-6">
          <DialogTitle className="text-lg">Podpod is sunsetting</DialogTitle>
          <DialogDescription>
            Thank you to everyone who listened along. Podpod is no longer generating podcasts and
            sign-ups are closed. For any questions or requests, reach us at{" "}
            <a href="mailto:contact@podpod.me" className="underline text-gray-12">
              contact@podpod.me
            </a>
            .
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}

function Header() {
  return (
    <header className="masthead">
      <Link to="/" aria-label="Podpod home">
        <PodpodLogo aria-hidden="true" />
      </Link>
      <a className="contact-link" href="mailto:contact@podpod.me">
        Contact <span aria-hidden="true">↗</span>
      </a>
    </header>
  )
}
const Steps = () => (
  <section className="steps landing-width" aria-label="How Podpod works">
    <div className="step-card">
      <p className="step-label">01 / Collect</p>
      <h2>Send us the content</h2>
      <div className="step-preview">
        <p>
          Prefix an article URL with <strong>podpod.me/</strong>
        </p>
        <PrefixDemo />
      </div>
      <div className="step-preview email-preview">
        <p>Or forward a newsletter to your Podpod email</p>
        <img src="/images/add-email.png" alt="" />
      </div>
    </div>
    <div className="step-card listen-step">
      <p className="step-label">02 / Listen</p>
      <h2>Get your podcast</h2>
      <p className="step-description">Your articles become episodes in your podcast app.</p>
      <img src="/images/create-podcast.webp" alt="Episodes in a Podpod feed" />
    </div>
  </section>
)

const hostShows = [
  {
    name: "Prompted",
    cover: "prompted-cover.jpg",
    description: "An easygoing, thoughtful conversation about what you’ve been reading.",
  },
  {
    name: "News Team",
    comingSoon: true,
    cover: "news-cover.webp",
    description: "Judy and Ben look beyond the headline and bring in context from across the web.",
  },
  {
    name: "Here For It",
    comingSoon: true,
    cover: "here-cover.webp",
    description: "Katy and Zoe are here for the gossip, the drama and all the details.",
  },
]

const Hosts = () => (
  <section className="host-section landing-width">
    <h2>Meet the hosts</h2>
    <p className="section-intro">Three shows, each with its own voice.</p>
    <div className="host-cards">
      {hostShows.map(({ name, cover, description, comingSoon }) => (
        <article key={name}>
          <img src={`/images/${cover}`} alt="" />
          <div>
            <h3>{name}</h3>
            <p>{description}</p>
            {comingSoon && <span className="host-status">Coming soon</span>}
          </div>
        </article>
      ))}
    </div>
  </section>
)

const Hero = () => {
  return (
    <section>
      <div className="hero-heading">
        <a
          className="launch-badge"
          href="https://www.producthunt.com/products/podpod?launch=podpod"
          title="Podpod on Product Hunt · 30 April 2025"
        >
          <strong>Product Hunt</strong>
          <span>#2 Product of the Day</span>
        </a>
        <h1>
          A podcast <span>made for you</span>
        </h1>
        <p className="text-sm md:text-base text-gray-11 mt-2">
          AI hosts talk through the articles and newsletters you don’t have time to read.
        </p>
      </div>
    </section>
  )
}

const FloatingArticles = () => {
  const [opacity, setOpacity] = useState(1)

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const newOpacity = Math.max(1 - scrollY / 500, 0)
      setOpacity(newOpacity)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <section>
      <div className="phone-scene">
        <div className="article-layer" style={{ opacity }}>
          <article className="relative hidden md:flex justify-between w-[232px] h-[62px] bg-light rounded-lg p-2 floating-ani-1 left-[-15%] top-[-30%]">
            <div>
              <div className="text-[11px] leading-snug text-gray-11">Arc Prize</div>
              <div className="text-xs pr-2">An Analysis of DeepSeek’s R1-Zero and R1</div>
            </div>
            <img
              src="/images/lander_news1.png"
              role="presentation"
              alt="Arc Prize news article thumbnail"
              className="rounded-lg"
            />
          </article>
          <article className="relative hidden md:flex justify-between w-[232px] h-[62px] bg-light rounded-lg p-2 floating-ani-2 left-[30%] top-[-43%] ">
            <div>
              <div className="text-[11px] leading-snug text-gray-11">The Conversation</div>
              <div className="text-xs pr-2">Why is it so difficult to make a new antibiotic?</div>
            </div>
            <img
              src="/images/lander_news2.png"
              role="presentation"
              alt="The Conversation news article thumbnail"
              className="rounded-lg"
            />
          </article>
          <article className="relative hidden md:flex justify-between w-[232px] h-[62px] bg-light rounded-lg p-2 floating-ani-3 left-[8%] top-[-10%]">
            <div>
              <div className="text-[11px] leading-snug text-gray-11">UN</div>
              <div className="text-xs pr-2">In an AI-enabled world what is education for?</div>
            </div>
            <img
              src="/images/lander_news3.png"
              role="presentation"
              alt="UN news article thumbnail about AI and education"
              className="rounded-lg"
            />
          </article>
        </div>
        {/* Phone */}
        <img
          src="/images/phone.webp"
          role="presentation"
          alt="Podpod mobile app interface showing podcast playing"
          className="relative w-[70%] sm:w-[50%] md:w-[25%] max-w-[500px]"
        />
      </div>
    </section>
  )
}

const Faqs = () => {
  return (
    <section className="faq-section landing-width">
      <div className="faq-layout">
        <h2>FAQs</h2>

        <div>
          <details className="border-b py-4">
            <summary className="cursor-pointer font-medium">What is Podpod?</summary>
            <p className="mt-3 text-sm text-gray-11">
              Podpod is an AI-powered tool that turns articles and newsletters into podcasts. Just
              add podpod.me/ in front of any URL or forward a newsletter — and Podpod delivers a
              narrated version to your podcast app.
            </p>
          </details>

          <details className="border-b py-4">
            <summary className="cursor-pointer font-medium">
              How do I turn an article into a podcast?
            </summary>
            <p className="mt-3 text-sm text-gray-11">
              Simply add podpod.me/ before the article's URL and hit enter. We'll convert the text
              into a podcast episode using an AI voice and send it to your personal feed.
            </p>
          </details>

          <details className="border-b py-4">
            <summary className="cursor-pointer font-medium">
              Can Podpod read newsletters too?
            </summary>
            <p className="mt-3 text-sm text-gray-11">
              Yes! Just forward any email newsletter to your personal Podpod email address. We’ll
              convert it into an audio episode and send it to your podcast feed.
            </p>
          </details>

          <details className="border-b py-4">
            <summary className="cursor-pointer font-medium">
              Which podcast apps does Podpod work with?
            </summary>
            <p className="mt-3 text-sm text-gray-11">
              Podpod works with most major podcast apps that support private RSS feeds, including
              Apple Podcasts, Pocket Casts, Overcast, and more.
            </p>
          </details>

          <details className="border-b py-4">
            <summary className="cursor-pointer font-medium">
              Does Podpod work with long-form content?
            </summary>
            <p className="mt-3 text-sm text-gray-11">
              Absolutely. Podpod is built to handle long-form articles and in-depth newsletters,
              breaking them down into listenable episodes that fit into your day.
            </p>
          </details>
        </div>
      </div>
    </section>
  )
}

const Footer = () => {
  return (
    <footer className="page-footer landing-width text-xs">
      <div className="footer-layout flex items-start justify-between">
        <div className="flex flex-col justify-between">
          <PodpodLogo className="w-[30px] h-[30px] text-gray-12" />
          <p className="mt-16 text-xs">Copyright © 2025 Podpod.me</p>
        </div>

        <nav className="flex gap-16 text-right">
          <div>
            <h4 className="text-gray-400 font-medium">Legal</h4>
            <ul className="space-y-1 mt-2">
              <li>
                <a href="/terms-of-service" className="hover:underline text-black">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="/privacy-policy" className="hover:underline text-black">
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-gray-400 font-medium">Social</h4>
            <ul className="space-y-1 mt-2">
              <li>
                <a href="https://www.threads.net/@podpodme" className="hover:underline text-black">
                  Threads
                </a>
              </li>
              <li>
                <a href="https://x.com/podpodme" className="hover:underline text-black">
                  X
                </a>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </footer>
  )
}
